import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import LinkIcon from '@mui/icons-material/Link'
import { useCustomMutation, useNotification } from '@refinedev/core'
import {
  QuestionDiffResponse,
  QuestionFormaterResponse,
  StructuredQuestion,
} from '../interfaces/questions'
import AIFormatReview from './QuestionFormattingReview'
import QuestionDiffView from './QuestionDiffView'

interface QuestionDiffCheckerProps {
  opportunityUuid: string
  existingStructuredQuestions: StructuredQuestion[]
  rawQuestions: string[]
  onAcceptQuestions: (questions: StructuredQuestion[]) => void
  applicationLink?: string
}

type Status = 'input' | 'loading' | 'result'

export default function QuestionDiffChecker({
  opportunityUuid,
  existingStructuredQuestions,
  onAcceptQuestions,
  applicationLink,
}: QuestionDiffCheckerProps) {
  const [status, setStatus] = useState<Status>('input')
  const [extractionMode, setExtractionMode] = useState<'file' | 'url'>(
    applicationLink ? 'url' : 'file',
  )
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [urlInput, setUrlInput] = useState(applicationLink || '')
  const [diffResponse, setDiffResponse] = useState<QuestionDiffResponse | null>(
    null,
  )
  const [extractionWarning, setExtractionWarning] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (applicationLink) {
      setUrlInput((current) => (current ? current : applicationLink))
    }
  }, [applicationLink])

  const { open: openNotification } = useNotification()
  const { mutate: extractAndCompare } =
    useCustomMutation<QuestionDiffResponse>()

  const canSubmit =
    extractionMode === 'file'
      ? selectedFile !== null
      : urlInput.trim().length > 0

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    setSelectedFile(file)
  }

  function handleUrlChange(e: React.ChangeEvent<HTMLInputElement>) {
    setUrlInput(e.target.value)
  }

  function handleModeChange(
    _: React.MouseEvent<HTMLElement>,
    newMode: 'file' | 'url' | null,
  ) {
    if (newMode) {
      setExtractionMode(newMode)
      setExtractionWarning('')
    }
  }

  function handleSubmit() {
    if (!canSubmit) return

    setStatus('loading')

    const formData = new FormData()
    if (extractionMode === 'file' && selectedFile) {
      formData.append('file', selectedFile)
    } else {
      formData.append('url', urlInput.trim())
    }
    if (existingStructuredQuestions.length > 0) {
      formData.append(
        'existing_questions_json',
        JSON.stringify(existingStructuredQuestions),
      )
    }

    extractAndCompare(
      {
        url: `/opportunities/${opportunityUuid}/questions/extract-and-compare`,
        method: 'post',
        values: formData,
        config: {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      },
      {
        onSuccess: (response) => {
          const data = response.data

          // 0 questions extracted
          if (data && data.parsed_questions.length === 0) {
            setExtractionWarning(
              extractionMode === 'file'
                ? 'No questions could be extracted from this file. The document may not contain application questions, or the format may not be supported. Try a different file or enter questions manually.'
                : 'No questions could be extracted from this website. The site may be blocking automated access. Try uploading a PDF or DOCX version of the application form instead or enter questions manually.',
            )
            setStatus('input')
            return
          }

          setExtractionWarning('')
          setDiffResponse(data)
          setStatus('result')
        },
        onError: (error: any) => {
          setStatus('input')
          const detail = error?.response?.data?.detail || error?.message || ''
          const isUrlError =
            extractionMode === 'url' &&
            (detail.toLowerCase().includes('url') || error?.response?.status === 400)

          if (isUrlError) {
            setExtractionWarning(
              'Could not extract content from this website. The site may be blocking automated access or the URL may be invalid. Try uploading a PDF or DOCX version of the application form instead.',
            )
          } else {
            openNotification?.({
              message: 'Failed to extract and compare questions',
              description: detail || 'Please try again',
              type: 'error',
            })
          }
        },
      },
    )
  }

  function handleReset() {
    setStatus('input')
    setDiffResponse(null)
    setSelectedFile(null)
    setUrlInput(applicationLink || '')
    setExtractionWarning('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Build the QuestionFormaterResponse shape expected by AIFormatReview
  function buildFormatResult(): QuestionFormaterResponse | null {
    if (!diffResponse) return null
    return {
      questions: diffResponse.parsed_questions,
      raw_text: diffResponse.extracted_text,
      confidence: diffResponse.confidence,
      parsing_notes: diffResponse.parsing_notes,
      ambiguities: diffResponse.ambiguities,
      warnings: diffResponse.warnings,
    }
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Check for Question Changes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload the funder's current application form (PDF or DOCX) or provide a
        URL to check whether the questions have changed since they were last
        updated. <br />
        This tool will compare to the existing structured questions on this
        form. Please read through the funder's application to make sure the
        suggested changes are correct.
      </Typography>

      {status === 'input' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxWidth: 600,
          }}
        >
          {extractionWarning && (
            <Alert severity="warning">{extractionWarning}</Alert>
          )}
          <ToggleButtonGroup
            value={extractionMode}
            exclusive
            onChange={handleModeChange}
            size="small"
          >
            <ToggleButton value="file">
              <UploadFileIcon sx={{ mr: 1 }} /> Upload File
            </ToggleButton>
            <ToggleButton value="url">
              <LinkIcon sx={{ mr: 1 }} /> From URL
            </ToggleButton>
          </ToggleButtonGroup>

          {extractionMode === 'file' && (
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                type="button"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? selectedFile.name : 'Choose File (PDF or DOCX)'}
              </Button>
            </Box>
          )}

          {extractionMode === 'url' && (
            <>
              {applicationLink && urlInput === applicationLink && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: -1 }}
                >
                  You can change the URL below. This is pre-filled from "Website
                  link to application" field:{' '}
                  <a
                    href={applicationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {applicationLink}
                  </a>
                </Typography>
              )}
              <TextField
                label="Application form URL"
                placeholder="https://example.com/apply"
                value={urlInput}
                onChange={handleUrlChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
              />
            </>
          )}

          <Box>
            <Button
              type="button"
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              Extract &amp; Compare
            </Button>
          </Box>
        </Box>
      )}

      {status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Extracting and comparing questions — this may take a moment...
          </Typography>
        </Box>
      )}

      {status === 'result' && diffResponse && (
        <>
          {diffResponse.has_existing_questions ? (
            // Diff view — show side-by-side comparison
            <QuestionDiffView
              diffResponse={diffResponse}
              onAcceptQuestions={(questions) => {
                onAcceptQuestions(questions)
                handleReset()
                openNotification?.({
                  message: 'Questions updated — click Save to persist changes',
                  type: 'success',
                })
              }}
              onBack={handleReset}
            />
          ) : (
            // No existing questions — fall through to the standard review/import modal
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                No existing structured questions found. Review the extracted
                questions below and accept to import them.
              </Alert>
              <Button
                type="button"
                variant="text"
                size="small"
                onClick={handleReset}
                sx={{ mb: 2 }}
              >
                ← Back
              </Button>
              <AIFormatReview
                open={true}
                rawText=""
                formatResult={buildFormatResult()}
                onClose={handleReset}
                onRetry={handleReset}
                hideRawText
                onAccept={(questions) => {
                  onAcceptQuestions(questions)
                  handleReset()
                  openNotification?.({
                    message:
                      'Questions imported — click Save to persist changes',
                    type: 'success',
                  })
                }}
              />
            </>
          )}
        </>
      )}
    </Box>
  )
}
